import { supabase } from './supabase';
import { MyMedication, MedicationScheduleEntry } from '../types';
import { isMedicationActive } from '../utils/dateHelpers';

// ---------- helpers for jsonb ----------
function parseJsonbArray(value: any): string[] | undefined {
  if (value === null || value === undefined) return undefined;

  // already an array (correct jsonb)
  if (Array.isArray(value)) return value as string[];

  // legacy: stored as stringified JSON
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed as string[];
      return undefined;
    } catch {
      return undefined;
    }
  }

  return undefined;
}

function parseJsonbAny<T = any>(value: any): T | undefined {
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'object') return value as T;

  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }

  return undefined;
}

/** Normalizuje taperingSchedules z DB - obsługa snake_case i brakujących pól */
function normalizeTaperingSchedules(raw: any): Array<{ startDate: string; endDate?: string; dosage: number; frequency: string; times?: string[] }> | undefined {
  const arr = Array.isArray(raw) ? raw : parseJsonbAny<any[]>(raw);
  if (!arr || arr.length === 0) return undefined;
  return arr.map((s: any) => ({
    startDate: s.startDate ?? s.start_date ?? '',
    endDate: s.endDate ?? s.end_date,
    dosage: s.dosage ?? 1,
    frequency: s.frequency ?? '1x dziennie',
    times: (s.times && Array.isArray(s.times)) ? s.times : undefined
  })).filter(s => s.startDate);
}

// ---------- date helpers ----------
function normalizeStartOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function normalizeEndOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function toDateOnlyString(d: Date) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function maxDate(a: Date, b: Date) {
  return a > b ? a : b;
}

function minDate(a: Date, b: Date) {
  return a < b ? a : b;
}

/** Czy lek miał aktywność w zakresie dat [rangeStart, rangeEnd].
 * Dla tapering: zakres z taperingSchedules (etapów), fallback na startDate/endDate rekordu.
 * Dla one-time: tylko dzień oneTimeDate.
 */
function isMedicationActiveInRange(medication: MyMedication, rangeStart: string, rangeEnd: string): boolean {
  if (medication.type === 'one-time') {
    if (!medication.oneTimeDate) return false;
    if (medication.endDate) return false;
    return medication.oneTimeDate >= rangeStart && medication.oneTimeDate <= rangeEnd;
  }
  if (medication.type === 'tapering') {
    if (medication.taperingSchedules && medication.taperingSchedules.length > 0) {
      const starts = medication.taperingSchedules.map(s => s.startDate).filter(Boolean);
      const ends = medication.taperingSchedules.map(s => (s.endDate || '9999-12-31')).filter(Boolean);
      if (starts.length && ends.length) {
        const medStart = starts.sort()[0];
        const medEnd = ends.sort().reverse()[0];
        return medStart <= rangeEnd && medEnd >= rangeStart;
      }
    }
    // Fallback: gdy brak taperingSchedules, używaj start_date/end_date z rekordu my_medications
  }
  if (!medication.startDate) return false;
  if (medication.startDate > rangeEnd) return false;
  const medEnd = medication.endDate || '9999-12-31';
  if (medEnd < rangeStart) return false;
  return true;
}

// Konwersja między formatem aplikacji a formatem bazy danych
const toDbFormat = (data: MyMedication, includeTimestamps: boolean = false) => {
  const result: any = {
    id: data.id,
    name: data.name,
    category: data.category || 'LEK',
    type: data.type,
    start_date: data.startDate,
    end_date: data.endDate || null,
    with_meal: data.withMeal || false,
    taking_instructions: data.takingInstructions || null,
    notes: data.notes || '',
    is_active: isMedicationActive(data)
  };

  // ✅ jsonb zapisujemy jako tablice/obiekty, NIE jako JSON.stringify
  if (data.type === 'permanent') {
    if (data.permanentDosage !== undefined && data.permanentDosage !== null) {
      result.permanent_dosage = data.permanentDosage;
    }
    if (data.permanentFrequency) {
      result.permanent_frequency = data.permanentFrequency;
    }
    if (data.permanentTimes && data.permanentTimes.length > 0) {
      result.permanent_times = data.permanentTimes; // jsonb array
    }
  } else if (data.type === 'temporary') {
    if (data.temporaryDosage !== undefined && data.temporaryDosage !== null) {
      result.temporary_dosage = data.temporaryDosage;
    }
    if (data.temporaryFrequency) {
      result.temporary_frequency = data.temporaryFrequency;
    }
    if (data.temporaryTimes && data.temporaryTimes.length > 0) {
      result.temporary_times = data.temporaryTimes; // jsonb array
    }
    if (data.temporaryDuration !== undefined && data.temporaryDuration !== null) {
      result.temporary_duration = data.temporaryDuration;
    }
  } else if (data.type === 'tapering') {
    if (data.taperingSchedules && data.taperingSchedules.length > 0) {
      result.tapering_schedules = data.taperingSchedules; // jsonb
    }
  } else if (data.type === 'one-time') {
    if (data.oneTimeDate) result.one_time_date = data.oneTimeDate;
    if (data.oneTimeTime) result.one_time_time = data.oneTimeTime;
    if (data.oneTimeDosage !== undefined && data.oneTimeDosage !== null) {
      result.one_time_dosage = data.oneTimeDosage;
    }
  }

  if (includeTimestamps) {
    if (data.createdAt) result.created_at = data.createdAt;
    if (data.updatedAt) result.updated_at = data.updatedAt;
  }

  return result;
};

const fromDbFormat = (dbData: any): MyMedication => ({
  id: dbData.id,
  name: dbData.name,
  category: (dbData.category === 'LEK' || dbData.category === 'SUPLEMENT') ? dbData.category : 'LEK',
  type: dbData.type,
  startDate: dbData.start_date,
  endDate: dbData.end_date,
  withMeal: dbData.with_meal || false,
  takingInstructions: dbData.taking_instructions || (dbData.with_meal ? 'with_meal' : null),
  notes: dbData.notes || '',
  permanentDosage: dbData.permanent_dosage,
  permanentFrequency: dbData.permanent_frequency,
  permanentTimes: parseJsonbArray(dbData.permanent_times),
  temporaryDosage: dbData.temporary_dosage,
  temporaryFrequency: dbData.temporary_frequency,
  temporaryTimes: parseJsonbArray(dbData.temporary_times),
  temporaryDuration: dbData.temporary_duration,
  taperingSchedules: normalizeTaperingSchedules(dbData.tapering_schedules ?? dbData.tapering_schedule),
  oneTimeDate: dbData.one_time_date,
  oneTimeTime: dbData.one_time_time,
  oneTimeDosage: dbData.one_time_dosage,
  isActive: dbData.is_active !== undefined ? dbData.is_active : true,
  createdAt: dbData.created_at,
  updatedAt: dbData.updated_at
});

const scheduleEntryToDb = (data: MedicationScheduleEntry) => ({
  id: data.id,
  my_medication_id: data.myMedicationId,
  date: data.date,
  time: data.time,
  dosage: data.dosage,
  taken: data.taken,
  taken_at: data.takenAt || null
});

const scheduleEntryFromDb = (dbData: any): MedicationScheduleEntry => ({
  id: dbData.id,
  myMedicationId: dbData.my_medication_id,
  date: dbData.date,
  time: dbData.time,
  dosage: dbData.dosage,
  taken: dbData.taken,
  takenAt: dbData.taken_at,
});

export const myMedicationService = {
  // Pobierz wszystkie moje leki
  getAll: async (): Promise<MyMedication[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');

    const { data, error } = await supabase
      .from('my_medications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Błąd pobierania moich leków:', error);
      throw error;
    }

    return (data || []).map(fromDbFormat);
  },

  // Utwórz nowy plan leczenia
  create: async (medication: MyMedication): Promise<MyMedication> => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('Użytkownik nie jest zalogowany');

    const dbData: any = {
      ...toDbFormat(medication, false),
      user_id: user.id
    };

    // usuń undefined/null dla opcjonalnych pól
    Object.keys(dbData).forEach(key => {
      if (key === 'notes' || key === 'with_meal' || key === 'is_active' || key === 'start_date' || key === 'name' || key === 'type' || key === 'user_id' || key === 'id') {
        return;
      }
      if (dbData[key] === undefined || dbData[key] === null) delete dbData[key];
    });

    const { data: insertedData, error } = await supabase
      .from('my_medications')
      .insert([dbData])
      .select()
      .single();

    if (error) {
      console.error('Błąd tworzenia planu leczenia:', error);
      throw error;
    }

    return fromDbFormat(insertedData);
  },

  // Zaktualizuj plan leczenia
  update: async (id: string, medication: MyMedication): Promise<MyMedication> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');

    const dbData: any = toDbFormat(medication, true);
    delete dbData.id;

    const { data: updatedData, error } = await supabase
      .from('my_medications')
      .update(dbData)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Błąd aktualizacji planu leczenia:', error);
      throw error;
    }

    return fromDbFormat(updatedData);
  },

  // Usuń plan leczenia (hard delete)
  delete: async (id: string): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');

    const { error: scheduleError } = await supabase
      .from('medication_schedule_entries')
      .delete()
      .eq('my_medication_id', id)
      .eq('user_id', user.id);

    if (scheduleError) {
      console.error('Błąd usuwania wpisów harmonogramu:', scheduleError);
      throw scheduleError;
    }

    const { error } = await supabase
      .from('my_medications')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Błąd usuwania planu leczenia:', error);
      throw error;
    }
  },

  // Generuj wpisy w kalendarzu na podstawie planu leczenia
  generateScheduleEntries: async (medication: MyMedication, startDate: string, endDate: string): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');

    console.log('[generateScheduleEntries]', { medicationId: medication.id, medicationName: medication.name, startDate, endDate, medicationStartDate: medication.startDate, medicationEndDate: medication.endDate });

    const requestedStart = normalizeStartOfDay(new Date(startDate + 'T12:00:00'));
    const requestedEnd = normalizeEndOfDay(new Date(endDate + 'T12:00:00'));

    const medStart = medication.startDate ? normalizeStartOfDay(new Date(medication.startDate + 'T12:00:00')) : requestedStart;

    let medEnd: Date | null = null;
    if (medication.endDate) {
      medEnd = normalizeEndOfDay(new Date(medication.endDate + 'T12:00:00'));
    }

    let effectiveStart = maxDate(requestedStart, medStart);
    let effectiveEnd = requestedEnd;
    if (medEnd) effectiveEnd = minDate(requestedEnd, medEnd);

    if (effectiveStart > effectiveEnd) return;

    const effectiveStartStr = toDateOnlyString(effectiveStart);
    const effectiveEndStr = toDateOnlyString(effectiveEnd);

    const entries: MedicationScheduleEntry[] = [];
    const current = new Date(effectiveStart);

    while (current <= effectiveEnd) {
      const dateStr = toDateOnlyString(current);

      if (medication.type === 'permanent' && medication.permanentDosage && medication.permanentFrequency) {
        const times =
          medication.permanentTimes && medication.permanentTimes.length > 0
            ? medication.permanentTimes
            : (medication.permanentFrequency.toLowerCase().includes('x dziennie')
                ? parseFrequencyToTimes(medication.permanentFrequency)
                : ['08:00']);

        times.forEach(time => {
          entries.push({
            id: `${medication.id}-${dateStr}-${time}`,
            myMedicationId: medication.id,
            date: dateStr,
            time,
            dosage: medication.permanentDosage || 1,
            taken: false
          });
        });
      }

      if (medication.type === 'temporary' && medication.temporaryDosage && medication.temporaryFrequency) {
        const times =
          medication.temporaryTimes && medication.temporaryTimes.length > 0
            ? medication.temporaryTimes
            : parseFrequencyToTimes(medication.temporaryFrequency);

        times.forEach(time => {
          entries.push({
            id: `${medication.id}-${dateStr}-${time}`,
            myMedicationId: medication.id,
            date: dateStr,
            time,
            dosage: medication.temporaryDosage || 1,
            taken: false
          });
        });
      }

      if (medication.type === 'tapering' && medication.taperingSchedules && Array.isArray(medication.taperingSchedules) && medication.taperingSchedules.length > 0) {
        medication.taperingSchedules.forEach((schedule: any) => {
          const schStart = schedule.startDate ?? schedule.start_date;
          const schEnd = schedule.endDate ?? schedule.end_date;
          if (!schStart) return;

          const scheduleStart = normalizeStartOfDay(new Date(schStart + 'T12:00:00'));
          const scheduleEnd = schEnd ? normalizeEndOfDay(new Date(schEnd + 'T12:00:00')) : requestedEnd;
          const scheduleDosage = schedule.dosage ?? 1;
          const scheduleFreq = schedule.frequency ?? '1x dziennie';

          if (current >= scheduleStart && current <= scheduleEnd) {
            const times =
              schedule.times && Array.isArray(schedule.times) && schedule.times.length > 0
                ? schedule.times
                : parseFrequencyToTimes(scheduleFreq);

            times.forEach((time: string) => {
              entries.push({
                id: `${medication.id}-${dateStr}-${time}-${schStart}`,
                myMedicationId: medication.id,
                date: dateStr,
                time,
                dosage: scheduleDosage,
                taken: false
              });
            });
          }
        });
      }

      if (medication.type === 'one-time' && medication.oneTimeDate === dateStr) {
        entries.push({
          id: `${medication.id}-${dateStr}`,
          myMedicationId: medication.id,
          date: dateStr,
          time: medication.oneTimeTime || '08:00',
          dosage: medication.oneTimeDosage || 1,
          taken: false
        });
      }

      current.setDate(current.getDate() + 1);
    }

    const { error: deleteError } = await supabase
      .from('medication_schedule_entries')
      .delete()
      .eq('user_id', user.id)
      .eq('my_medication_id', medication.id)
      .eq('taken', false)
      .gte('date', effectiveStartStr)
      .lte('date', effectiveEndStr);

    if (deleteError) {
      console.error('Błąd usuwania starych wpisów harmonogramu:', deleteError);
      throw deleteError;
    }

    if (entries.length === 0) return;

    const dbEntries = entries.map(e => ({
      ...scheduleEntryToDb(e),
      user_id: user.id
    }));

    const { error: upsertError } = await supabase
      .from('medication_schedule_entries')
      .upsert(dbEntries, { onConflict: 'id', ignoreDuplicates: true });

    if (upsertError) {
      console.error('Błąd generowania wpisów w kalendarzu:', upsertError);
      throw upsertError;
    }
  },

 // ✅ Pobierz wpisy z kalendarza dla dokładnie podanego zakresu dat
getScheduleEntries: async (startDate: string, endDate: string, mode: 'today' | 'calendar' = 'calendar'): Promise<MedicationScheduleEntry[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Użytkownik nie jest zalogowany');

  console.log('GET SCHEDULE RANGE:', { startDate, endDate, mode });

  const startISO = startDate;
  const endISO = endDate;

  const { data: allMedications, error: medError } = await supabase
    .from('my_medications')
    .select('*')
    .eq('user_id', user.id);

  if (medError) {
    console.error('Błąd pobierania leków:', medError);
    throw medError;
  }

  const medications = (allMedications || []).map((r: any) => fromDbFormat(r));
  const filteredIds = mode === 'today'
    ? medications.filter((m: MyMedication) => isMedicationActive(m)).map(m => m.id)
    : medications
        .filter((m: MyMedication) => isMedicationActiveInRange(m, startDate, endDate))
        .map(m => m.id);

  if (filteredIds.length === 0) return [];

  const { data, error } = await supabase
    .from('medication_schedule_entries')
    .select('*')
    .eq('user_id', user.id)
    .in('my_medication_id', filteredIds)
    .gte('date', startISO)
    .lte('date', endISO)
    .order('date', { ascending: true })
    .order('time', { ascending: true });

  if (error) {
    console.error('Błąd pobierania wpisów z kalendarza:', error);
    throw error;
  }

  console.log('[getScheduleEntries] supabase returned:', (data || []).length);

  return (data || []).map(scheduleEntryFromDb);
},


  /** Usuń WSZYSTKIE wpisy harmonogramu dla leku (również taken=true). Do jednorazowej naprawy dat. */
  deleteAllScheduleEntriesForMedication: async (medicationId: string): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');

    const { error } = await supabase
      .from('medication_schedule_entries')
      .delete()
      .eq('user_id', user.id)
      .eq('my_medication_id', medicationId);

    if (error) {
      console.error('Błąd usuwania wpisów harmonogramu:', error);
      throw error;
    }
  },

  /** Sprawdza wszystkie leki i dla tych z 0 wpisów w harmonogramie generuje wpisy od startDate do końca 2027. */
  ensureMedicationsHaveSchedules: async (): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const medications = await myMedicationService.getAll();
    const endOf2027 = '2027-12-31';
    for (const med of medications) {
      const { count } = await supabase
        .from('medication_schedule_entries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('my_medication_id', med.id);
      const countVal = count ?? 0;
      let rangeStart = med.startDate;
      let rangeEnd = endOf2027;
      if (med.type === 'tapering' && med.taperingSchedules?.length) {
        const starts = med.taperingSchedules.map((s: any) => s.startDate ?? s.start_date).filter(Boolean);
        const ends = med.taperingSchedules.map((s: any) => (s.endDate ?? s.end_date) || endOf2027).filter(Boolean);
        if (starts.length) rangeStart = starts.sort()[0];
        if (ends.length) rangeEnd = ends.sort().reverse()[0];
      } else if (med.endDate) {
        rangeEnd = med.endDate;
      }

      console.log('[ensureMedicationsHaveSchedules]', {
        nazwa: med.name,
        type: med.type,
        count: countVal,
        willGenerate: countVal === 0 && !!rangeStart,
        rangeStart,
        rangeEnd
      });
      if (countVal === 0 && !!rangeStart) {
        try {
          await myMedicationService.generateScheduleEntries(med, rangeStart, rangeEnd);
          console.log('[ensureMedicationsHaveSchedules] sukces:', med.name);
        } catch (err) {
          console.error('[ensureMedicationsHaveSchedules] błąd dla', med.name, err);
        }
      }
    }
  },

  // Oznacz lek jako przyjęty
  markAsTaken: async (entryId: string, taken: boolean): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Użytkownik nie jest zalogowany');

    const updateData: any = { taken };
    updateData.taken_at = taken ? new Date().toISOString() : null;

    const { error } = await supabase
      .from('medication_schedule_entries')
      .update(updateData)
      .eq('id', entryId)
      .eq('user_id', user.id);

    if (error) {
      console.error('Błąd aktualizacji wpisu:', error);
      throw error;
    }
  }
};

// Pomocnicza funkcja do parsowania częstotliwości na godziny
function parseFrequencyToTimes(frequency: string): string[] {
  const freqLower = frequency.toLowerCase().trim();

  const match = freqLower.match(/(\d+)x\s*dziennie/);
  if (match) {
    const count = parseInt(match[1]);
    if (count === 1) return ['08:00'];
    if (count === 2) return ['08:00', '20:00'];
    if (count === 3) return ['08:00', '14:00', '20:00'];
    if (count === 4) return ['08:00', '12:00', '16:00', '20:00'];

    const times: string[] = [];
    const startHour = 8;
    const endHour = 20;
    const interval = (endHour - startHour) / (count - 1);

    for (let i = 0; i < count; i++) {
      const hour = Math.round(startHour + i * interval);
      times.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return times;
  }

  if (freqLower.includes('rano')) return ['08:00'];
  if (freqLower.includes('wieczorem') || freqLower.includes('na noc') || freqLower.includes('wieczór')) return ['20:00'];
  if (freqLower.includes('po obiedzie')) return ['14:00'];
  if (freqLower.includes('przed snem')) return ['21:00'];
  if (freqLower.includes('co 8 godzin') || freqLower.includes('co 8h')) return ['08:00', '16:00', '00:00'];
  if (freqLower.includes('co 12 godzin') || freqLower.includes('co 12h')) return ['08:00', '20:00'];
  if (freqLower.includes('co 6 godzin') || freqLower.includes('co 6h')) return ['08:00', '14:00', '20:00', '02:00'];

  return ['08:00'];
}
